import { create } from 'zustand';
import * as authClient from '@/services/authClient';
import { clearPanelRedirectGuard, isSafeReturnPath } from '@/utils/panelRedirect';

const AVATAR_PLACEHOLDER = 'https://i.pravatar.cc/160?img=68';

const LS_KEY = 'ch_member';
const setMemberFlag = () => { try { localStorage.setItem(LS_KEY, '1'); } catch (_) {} };
const clearMemberFlag = () => {
  try { localStorage.removeItem(LS_KEY); } catch (_) {}
  clearPanelRedirectGuard();
};
export const isMemberBrowser = () => { try { return localStorage.getItem(LS_KEY) === '1'; } catch (_) { return false; } };

const mapUser = (raw) => ({
  ...raw,
  name: raw.full_name || raw.email?.split('@')[0] || '',
  avatar: AVATAR_PLACEHOLDER,
});

let _sessionExpiredHandling = false;

function handleSessionExpiredRedirect() {
  if (typeof window === 'undefined') return;
  if (_sessionExpiredHandling) return;
  const path = window.location.pathname || '';
  if (path === '/login' || path.startsWith('/login/') || path.startsWith('/auth/')) return;
  _sessionExpiredHandling = true;
  import('sonner').then(({ toast }) => {
    toast.error('Your session expired. Please sign in again.');
  }).catch(() => {});
  const returnTo = `${window.location.pathname}${window.location.search}`;
  const safe = isSafeReturnPath(returnTo) ? returnTo : null;
  window.location.assign(safe ? `/login?redirect=${encodeURIComponent(safe)}` : '/login');
}

export const useAuth = create((set, get) => ({
  user: null,
  features: [],
  subscription: null,
  isAuthenticated: false,
  isEmailVerified: false,
  bootstrapping: true,

  login: async (email, password) => {
    const data = await authClient.login(email, password);
    const user = mapUser(data.user);
    set({
      user,
      features: data.user.features || [],
      subscription: data.user.subscription || null,
      isAuthenticated: true,
      isEmailVerified: data.user.email_verified ?? false,
    });
    setMemberFlag();
    _sessionExpiredHandling = false;
    return data;
  },

  loginWithGoogle: async (credential) => {
    const payload =
      typeof credential === 'string'
        ? { id_token: credential }
        : {
            id_token: credential?.id_token,
            access_token: credential?.access_token,
          };
    const data = await authClient.socialLoginGoogle(payload);
    const user = mapUser(data.user);
    set({
      user,
      features: data.user.features || [],
      subscription: data.user.subscription || null,
      isAuthenticated: true,
      isEmailVerified: data.user.email_verified ?? false,
    });
    setMemberFlag();
    _sessionExpiredHandling = false;
    return data;
  },

  loginWithFacebook: async (access_token) => {
    const data = await authClient.socialLoginFacebook(access_token);
    const user = mapUser(data.user);
    set({
      user,
      features: data.user.features || [],
      subscription: data.user.subscription || null,
      isAuthenticated: true,
      isEmailVerified: data.user.email_verified ?? false,
    });
    setMemberFlag();
    _sessionExpiredHandling = false;
    return data;
  },

  loginWithApple: async (identity_token, user) => {
    const data = await authClient.socialLoginApple(identity_token, user);
    const mappedUser = mapUser(data.user);
    set({
      user: mappedUser,
      features: data.user.features || [],
      subscription: data.user.subscription || null,
      isAuthenticated: true,
      isEmailVerified: data.user.email_verified ?? false,
    });
    setMemberFlag();
    _sessionExpiredHandling = false;
    return data;
  },

  register: async (payload) => {
    const data = await authClient.register(payload);
    return data;
  },

  logout: async () => {
    try {
      await authClient.logout();
    } catch (_) {}
    clearMemberFlag();
    set({ user: null, features: [], subscription: null, isAuthenticated: false, isEmailVerified: false });
  },

  forceLogout: () => {
    clearMemberFlag();
    set({ user: null, features: [], subscription: null, isAuthenticated: false, isEmailVerified: false, bootstrapping: false });
    handleSessionExpiredRedirect();
  },

  refreshMe: async () => {
    if (!isMemberBrowser()) {
      set({ bootstrapping: false });
      return;
    }
    try {
      const data = await authClient.getMe();
      const user = mapUser(data);
      set({
        user,
        features: data.features || [],
        subscription: data.subscription || null,
        isAuthenticated: true,
        isEmailVerified: data.email_verified ?? false,
        bootstrapping: false,
      });
      setMemberFlag();
    } catch (_) {
      clearMemberFlag();
      set({ user: null, features: [], subscription: null, isAuthenticated: false, bootstrapping: false });
    }
  },

  updateProfile: async (patch) => {
    const data = await authClient.updateProfile(patch);
    const user = mapUser(data);
    set((state) => ({
      user: { ...state.user, ...user },
    }));
    return data;
  },

  hasPaidSubscription: () => {
    const { subscription } = get();
    if (!subscription) return false;
    const isPaid = ['active', 'trialing'].includes(subscription.status) && subscription.package_slug !== 'consumer-free';
    return isPaid;
  },

  isPanelUser: () => ['customer', 'contractor', 'staff'].includes(get().user?.kind),

  hasFeature: (key) => get().features.includes(key),
}));
